import { Controller } from "@hotwired/stimulus"
import { security_services_patrols_patrol_file_preview_status_path, security_services_patrols_patrol_file_preview_path } from "../routes"

// Connects to data-controller="patrol-file-preview"
export default class extends Controller {
  static targets = ["container"]
  static values = {
    url: String,
    fileId: Number
  }

  connect() {
    // Only start polling if the element is in processing state
    // Check if this is a processing container (has spinner)
    const isProcessing = this.element.classList.contains("processing") || 
                        this.element.querySelector(".spinner-border") !== null ||
                        this.element.querySelector(".processing") !== null;
    
    if (isProcessing) {
      // Ensure processing class is set
      if (this.hasContainerTarget && !this.containerTarget.classList.contains("processing")) {
        this.containerTarget.classList.add("processing");
      }
      
      // Check the initial status only for processing files
      this.checkPreviewStatus(true);
    }
    // If not processing, do nothing - the preview is already ready
  }

  startPolling() {
    this.stopPolling()
    
    this.statusTimer = setInterval(() => {
      this.checkPreviewStatus()
    }, 2000)
  }
  
  stopPolling() {
    if (this.statusTimer) {
      clearInterval(this.statusTimer)
      this.statusTimer = null
    }
  }

  async checkPreviewStatus(isInitialCheck = false) {
    try {
      const statusResponse = await fetch(security_services_patrols_patrol_file_preview_status_path(this.fileIdValue))
      
      if (!statusResponse.ok) {
        return
      }
      
      const statusData = await statusResponse.json()
      
      // Stop polling for files in final state
      if (statusData.status === 'previewable' || statusData.status === 'not_previewable' || 
          statusData.preview_state === 'success' || statusData.preview_state === 'failed' || 
          statusData.preview_state === 'protected' || statusData.preview_state === 'error') {
        
        this.stopPolling()
        
        // Always refresh the view when file reaches a final state (success or error)
        // This ensures the view updates for "success" states too, not just on errors or initial check
        this.forceRefreshView()
        
        // Remove processing class if it exists
        if (this.hasContainerTarget && this.containerTarget.classList.contains("processing")) {
          this.containerTarget.classList.remove("processing")
        }
        
        return
      }
      
      // For files still processing - only start polling if it's the initial check
      if (statusData.status === 'processing' || statusData.preview_state === 'processing' || 
          statusData.preview_state === 'pending') {
        if (this.hasContainerTarget && !this.containerTarget.classList.contains("processing")) {
          this.containerTarget.classList.add("processing")
          this.forceRefreshView()
        }
        
        // Start polling only if it's the initial check and we're not polling already
        if (isInitialCheck && !this.statusTimer) {
          this.startPolling()
        }
        return
      }
      
      // If status is unknown or unexpected, stop polling to avoid infinite loops
      this.stopPolling()
      
    } catch (error) {
      this.stopPolling()
    }
  }
  
  async forceRefreshView() {
    try {
      const url = new URL(security_services_patrols_patrol_file_preview_path(this.fileIdValue), window.location.origin);
      
      const response = await fetch(url, {
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
          'Accept': 'text/vnd.turbo-stream.html'
        }
      })
      
      if (response.ok) {
        const html = await response.text();
        this.updatePreviewContent(html);
      }
    } catch (error) {
      // Error handled silently
    }
  }
  
  updatePreviewContent(html) {
    // Use Turbo API for clean DOM updates
    window.Turbo.renderStreamMessage(html)
  }
  
  disconnect() {
    this.stopPolling()
  }
}